import fetchPerlgemRPC from '../../utils/perlgem_rpc';
import { eventRegistry } from '../events/registry';

interface IDataLayerEvent {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: { event_data?: any };
}

// TODO: update with full interfaces for all data layer properties
export interface IDataLayer {
  datalayer_events?: IDataLayerEvent;
  datalayer_events_processed?: IDataLayerEvent;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export class DataLayer {
  data: IDataLayer;

  constructor() {
    window.utag_data = window.utag_data || {};
    this.data = window.utag_data;
  }

  async load(): Promise<void> {
    const ssrDataLayer = this.getSSRDataLayer();

    let dataLayer;
    if (ssrDataLayer.__be_platform === 'bifrost') {
      dataLayer = { ...ssrDataLayer };
    } else {
      try {
        const perlgemDataLayer = await this.getPerlgemDataLayer();
        dataLayer = { ...perlgemDataLayer, ...ssrDataLayer };
      } catch (error) {
        dataLayer = { ...ssrDataLayer };
      }
    }

    this.enrichDataEvents(dataLayer);
    this.enrichDataProducts(dataLayer);

    Object.assign(this.data, dataLayer);
  }

  async getPerlgemDataLayer(): Promise<IDataLayer> {
    return (
      (await fetchPerlgemRPC({
        method: 'analytics.getDataLayer',
        params: [{}]
      })) || {}
    );
  }

  getSSRDataLayer(): IDataLayer {
    return window.page_data?.['analytics-datalayer'] ?? {};
  }

  enrichDataEvents(dataLayer: IDataLayer): void {
    dataLayer.datalayer_events = dataLayer.datalayer_events || {};
    dataLayer.datalayer_events_processed = dataLayer.datalayer_events_processed || {};

    const dleCookie = this.getDataLayerEventsCookie();
    const cookiedEvents = dleCookie ? dleCookie.split(',') : [];

    for (let i = 0; i < cookiedEvents.length; i++) {
      if (dataLayer.datalayer_events[cookiedEvents[i]]) {
        continue;
      }
      dataLayer.datalayer_events[cookiedEvents[i]] = {};
    }
  }

  enrichDataProducts(dataLayer: IDataLayer): void {
    if (dataLayer.product_impression_id) {
      dataLayer.product_impression_list = new Array(dataLayer.product_impression_id.length).fill(
        document.location.pathname
      );
      dataLayer.product_impression_position = dataLayer.product_impression_id.map(
        (_, i: number) => i + 1
      );
      dataLayer.product_impression_brand = dataLayer.product_impression_id.map(
        () => dataLayer.brand
      );
    }
    if (Array.isArray(dataLayer.cart_product_id)) {
      dataLayer.cart_product_brand = dataLayer.cart_product_id.map(() => dataLayer.brand);
    }
    if (Array.isArray(dataLayer.product_id)) {
      dataLayer.product_brand = dataLayer.product_id.map(() => dataLayer.brand);
    }
  }

  update(dataLayer: IDataLayer): void {
    Object.assign(this.data, dataLayer);
    if (dataLayer?.datalayer_events) {
      this.processEvents();
    }
  }

  processEvents(): void {
    const events = this.data.datalayer_events || {};

    if (Object.entries(events).length) {
      Object.entries(events).forEach(([eventName, eventData]) => {
        eventRegistry[eventName]?.(eventName, eventData?.event_data);
      });

      this.clearEvents();
    }
  }

  async clearEvents(): Promise<void> {
    if (this.getDataLayerEventsCookie()) {
      document.cookie = 'datalayer_events=0;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    } else {
      // RPC to server to delete all session events
      await fetchPerlgemRPC({ method: 'analytics.deleteEvents' });
    }

    this.data.datalayer_events_processed = {
      ...(this.data.datalayer_events_processed || {}),
      ...this.data.datalayer_events
    };
    this.data.datalayer_events = {};
  }

  getDataLayerEventsCookie(): string {
    return (
      document.cookie
        .split('; ')
        .find((item: string) => item.startsWith('datalayer_events='))
        ?.split('=')[1] || ''
    );
  }

  formatProductEventLabel(eventData: IDataLayer = this.data): string {
    return eventData.product_name && eventData.product_id
      ? `${this.stripOutMarkup(eventData.product_name[0])} - ${eventData.product_id[0]}`
      : '';
  }

  stripOutMarkup(str = ''): string {
    return str.replace(/(<([^>]+)>)/gi, '');
  }
}
